import React, { useState } from 'react';

export default function ContactForm({
  title = 'Send en besked',
  subtitle = 'Udfyld formularen, så vender vi hurtigt tilbage til dig.',
  isVikarForm = false,
}) {
  const [formData, setFormData] = useState({
    navn: '',
    telefon: '',
    email: '',
    faggruppe: '',
    beskrivelse: '',
    website_url: '', // Honeypot
  });

  const [status, setStatus] = useState({
    submitting: false,
    success: false,
    error: null,
  });

  const [errors, setErrors] = useState({});

  const validate = () => {
    const errs = {};
    if (!formData.navn.trim()) {
      errs.navn = 'Venligst indtast dit fulde navn.';
    }
    if (!formData.telefon.trim()) {
      errs.telefon = 'Venligst indtast dit telefonnummer.';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst indtast din e-mailadresse.';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errs.email = 'Venligst indtast en gyldig e-mailadresse.';
    }
    if (isVikarForm && !formData.faggruppe) {
      errs.faggruppe = 'Venligst vælg din faggruppe eller erfaringsbaggrund.';
    }
    if (!formData.beskrivelse.trim()) {
      errs.beskrivelse = isVikarForm
        ? 'Venligst skriv lidt om din erfaring og motivation.'
        : 'Venligst skriv en kort beskrivelse af din henvendelse.';
    }
    return errs;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setStatus({ submitting: true, success: false, error: null });

    try {
      const payload = {
        navn: formData.navn,
        telefon: formData.telefon,
        email: formData.email,
        beskrivelse: isVikarForm && formData.faggruppe
          ? `[Vikaransøgning - Faggruppe: ${formData.faggruppe}]\n\n${formData.beskrivelse}`
          : formData.beskrivelse,
        website_url: formData.website_url, // Honeypot
      };

      if (isVikarForm && formData.faggruppe) {
        payload.faggruppe = formData.faggruppe;
      }

      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (response.ok) {
        setStatus({ submitting: false, success: true, error: null });
        setFormData({
          navn: '',
          telefon: '',
          email: '',
          faggruppe: '',
          beskrivelse: '',
          website_url: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen.');
      }
    } catch (err) {
      setStatus({
        submitting: false,
        success: false,
        error: err.message || 'Der kunne ikke oprettes forbindelse til serveren. Ring gerne direkte på (+45) 23 96 40 42.',
      });
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 p-6 sm:p-8">
      {title && <h3 className="text-xl sm:text-2xl font-bold text-[#041c2c] tracking-tight mb-1">{title}</h3>}
      {subtitle && <p className="text-slate-600 text-sm mb-6">{subtitle}</p>}

      {status.success ? (
        <div className="p-6 bg-emerald-50 border border-emerald-200 rounded-lg text-center space-y-3 animate-in fade-in">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center mx-auto text-xl">
            <i className="fa-solid fa-check" aria-hidden="true"></i>
          </div>
          <h4 className="text-lg font-bold text-emerald-900">
            {isVikarForm ? 'Tak for din ansøgning!' : 'Tak for din henvendelse!'}
          </h4>
          <p className="text-sm text-emerald-800">
            {isVikarForm
              ? 'Vi har modtaget dine oplysninger og glæder os til at læse din henvendelse. Vi kontakter dig hurtigst muligt.'
              : 'Vi har modtaget din besked og kontakter dig hurtigst muligt. Hvis det haster, kan du altid ringe direkte til os på (+45) 23 96 40 42.'}
          </p>
          <button
            type="button"
            onClick={() => setStatus({ submitting: false, success: false, error: null })}
            className="mt-3 inline-flex items-center text-xs font-semibold text-emerald-700 hover:text-emerald-900 underline"
          >
            {isVikarForm ? 'Send en ny henvendelse' : 'Send en ny besked'}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
          {/* Honeypot field - strictly hidden with sr-only */}
          <div className="sr-only absolute -left-[9999px] top-auto">
            <label htmlFor="website_url">Website URL (leave blank)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex={-1}
              autoComplete="off"
            />
          </div>

          {status.error && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700 flex items-start gap-2.5">
              <i className="fa-solid fa-circle-exclamation mt-0.5 text-red-500 shrink-0" aria-hidden="true"></i>
              <span>{status.error}</span>
            </div>
          )}

          {/* Navn */}
          <div>
            <label htmlFor="navn" className="block text-sm font-semibold text-slate-700 mb-1">
              Navn <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              id="navn"
              name="navn"
              value={formData.navn}
              onChange={handleChange}
              placeholder="F.eks. Camilla Hansen"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm text-slate-900 focus:outline-none focus:ring-2 transition-all ${
                errors.navn
                  ? 'border-red-400 bg-red-50/30 focus:ring-red-300'
                  : 'border-slate-300 focus:border-[#b39366] focus:ring-[#b39366]/20'
              }`}
            />
            {errors.navn && <p className="text-red-600 text-xs mt-1">{errors.navn}</p>}
          </div>

          {/* Telefon */}
          <div>
            <label htmlFor="telefon" className="block text-sm font-semibold text-slate-700 mb-1">
              Telefonnummer <span className="text-red-500">*</span>
            </label>
            <input
              type="tel"
              id="telefon"
              name="telefon"
              value={formData.telefon}
              onChange={handleChange}
              placeholder="F.eks. 12 34 56 78"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm text-slate-900 focus:outline-none focus:ring-2 transition-all ${
                errors.telefon
                  ? 'border-red-400 bg-red-50/30 focus:ring-red-300'
                  : 'border-slate-300 focus:border-[#b39366] focus:ring-[#b39366]/20'
              }`}
            />
            {errors.telefon && <p className="text-red-600 text-xs mt-1">{errors.telefon}</p>}
          </div>

          {/* Email */}
          <div>
            <label htmlFor="email" className="block text-sm font-semibold text-slate-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="f.eks. navn@domæne.dk"
              className={`w-full px-4 py-2.5 rounded-lg border text-sm text-slate-900 focus:outline-none focus:ring-2 transition-all ${
                errors.email
                  ? 'border-red-400 bg-red-50/30 focus:ring-red-300'
                  : 'border-slate-300 focus:border-[#b39366] focus:ring-[#b39366]/20'
              }`}
            />
            {errors.email && <p className="text-red-600 text-xs mt-1">{errors.email}</p>}
          </div>

          {/* Faggruppe (Only for Vikar form) */}
          {isVikarForm && (
            <div>
              <label htmlFor="faggruppe" className="block text-sm font-semibold text-slate-700 mb-1">
                Faglig baggrund / faggruppe <span className="text-red-500">*</span>
              </label>
              <select
                id="faggruppe"
                name="faggruppe"
                value={formData.faggruppe}
                onChange={handleChange}
                className={`w-full px-4 py-2.5 rounded-lg border text-sm text-slate-900 bg-white focus:outline-none focus:ring-2 transition-all ${
                  errors.faggruppe
                    ? 'border-red-400 bg-red-50/30 focus:ring-red-300'
                    : 'border-slate-300 focus:border-[#b39366] focus:ring-[#b39366]/20'
                }`}
              >
                <option value="">Vælg din baggrund...</option>
                <option value="Social- og sundhedsassistent (SSA)">Social- og sundhedsassistent (SSA)</option>
                <option value="Social- og sundhedshjælper (SSH)">Social- og sundhedshjælper (SSH)</option>
                <option value="Pædagog / Socialpædagog">Pædagog / Socialpædagog</option>
                <option value="Pædagogisk assistent (PAU)">Pædagogisk assistent (PAU)</option>
                <option value="Sygeplejerske">Sygeplejerske</option>
                <option value="Ergoterapeut">Ergoterapeut</option>
                <option value="Person med min. 3 års relevant erhvervserfaring">Person med min. 3 års relevant erhvervserfaring</option>
                <option value="Anden relevant faglig baggrund">Anden relevant faglig baggrund</option>
              </select>
              {errors.faggruppe && <p className="text-red-600 text-xs mt-1">{errors.faggruppe}</p>}
            </div>
          )}

          {/* Beskrivelse */}
          <div>
            <label htmlFor="beskrivelse" className="block text-sm font-semibold text-slate-700 mb-1">
              {isVikarForm ? 'Kort om din erfaring og motivation' : 'Beskrivelse'}{' '}
              <span className="text-red-500">*</span>
            </label>
            <textarea
              id="beskrivelse"
              name="beskrivelse"
              rows={4}
              value={formData.beskrivelse}
              onChange={handleChange}
              placeholder={
                isVikarForm
                  ? 'Skriv kort om din erfaring, hvornår du kan starte, og hvilke vagttyper (dag, aften, nat, døgn) du er interesseret i...'
                  : 'Beskriv jeres behov for vikar, målgruppe, tidsramme eller spørgsmål...'
              }
              className={`w-full px-4 py-2.5 rounded-lg border text-sm text-slate-900 focus:outline-none focus:ring-2 transition-all ${
                errors.beskrivelse
                  ? 'border-red-400 bg-red-50/30 focus:ring-red-300'
                  : 'border-slate-300 focus:border-[#b39366] focus:ring-[#b39366]/20'
              }`}
            ></textarea>
            {errors.beskrivelse && <p className="text-red-600 text-xs mt-1">{errors.beskrivelse}</p>}
          </div>

          {/* Submit button */}
          <button
            type="submit"
            disabled={status.submitting}
            className="w-full inline-flex items-center justify-center gap-2 bg-[#b39366] hover:bg-[#a18155] disabled:bg-slate-400 text-white font-semibold py-3 px-6 rounded shadow-sm hover:shadow transition-all text-sm cursor-pointer"
          >
            {status.submitting ? (
              <>
                <i className="fa-solid fa-spinner fa-spin" aria-hidden="true"></i>
                <span>Sender...</span>
              </>
            ) : (
              <>
                <span>{isVikarForm ? 'Send vikaransøgning' : 'Send henvendelse'}</span>
                <i className="fa-solid fa-paper-plane text-xs" aria-hidden="true"></i>
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
